import { Button, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { RichTextEditor } from 'src/Components/Shared/RichTextEditor';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { DEFAULT_UPLOAD_FOLDER, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';

import styles from './IdeaForm.module.scss';
import { Props } from './IdeaForm.types';

const IdeaForm = ({ form, onSubmit, onCancel, isLoading }: Props) => {
  const { t } = useTranslation();

  const {
    uploads,
    startUpload,
    removeUpload,
    isLoading: isUploadLoading,
  } = useFileUploadManager({
    onUploadReady: readyFile => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      const attachment = {
        video: readyFile.videoData,
        fileId: readyFile.fileData.id,
        type: readyFile.fileData.type,
        name: readyFile.fileData.name,
        previewUrl: readyFile.fileData.urls?.previewThumbnailUrl,
      };

      form.setFieldValue('attachments', current => [...current, attachment]);
    },
  });
  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const newAttachments = await Promise.all(
      fileIds.map(async fileId => {
        const file = await fetchFileInfo(fileId);
        return {
          fileId: file.id,
          name: file.name,
          previewUrl: file.urls?.previewThumbnailUrl,
          type: file.type,
          video: file.video,
        };
      })
    );
    form.setFieldValue('attachments', current => [...current, ...newAttachments]);
  };

  return (
    <form onSubmit={onSubmit}>
      <div className={styles.formStack}>
        <TextInput
          label={t('ideas.form.title', "Titre de l'idée")}
          placeholder={t('ideas.form.titlePlaceholder', 'Ex: Installer un potager')}
          required
          {...form.getInputProps('title')}
        />

        <div>
          <div className={styles.label}>{t('common.description', 'Description')}</div>
          <RichTextEditor
            value={form.values.description}
            onChange={value => form.setFieldValue('description', value as SlateNode[])}
            placeholder={t('ideas.form.descriptionPlaceholder', 'Décrivez votre idée...')}
          />
        </div>

        <div className={styles.attachmentColumn}>
          <UploadFileButton
            onFilesSelected={files =>
              startUpload(files, DEFAULT_UPLOAD_FOLDER, 'PROJECT_ATTACHMENT')
            }
            onExistingFilesSelect={handleExistingFilesSelect}
            loading={isUploadLoading}
            modalTitle={t('idea.form.attachments.modalTitle', 'Ajouter des pièces jointes')}
          />

          <FormAttachmentViewer
            attachments={form.values.attachments}
            uploadingAttachments={uploads}
            onDeleteAttachment={attachment => {
              form.setFieldValue(
                'attachments',
                form.values.attachments.filter(a => a.fileId !== attachment.fileId)
              );
            }}
            onDeleteUploadingAttachment={localId => removeUpload(localId)}
          />
        </div>

        <div className={styles.buttonGroup}>
          <Button variant="light" onClick={onCancel}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button type="submit" loading={isLoading}>
            {t('ideas.form.submit', 'Proposer mon idée')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default IdeaForm;
